import React from "react";
import PropTypes from "prop-types";
import Modal from "@/components/ui/modal";
import { Clock, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const StatusHistoryModal = ({
  show,
  onHide,
  selectedRepairJob,
  statusEntries,
  handleStatusUpdate,
}) => {
  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        selectedRepairJob
          ? `Status History - ${selectedRepairJob.item_description}`
          : "Status History"
      }
      icon={Clock}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end w-full">
          <Button variant="outline" onClick={onHide}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {!statusEntries || statusEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
            <h5 className="text-lg font-medium text-muted-foreground">
              No Status History Found
            </h5>
            <Button
              className="mt-4 gap-2"
              onClick={() => {
                onHide();
                handleStatusUpdate(selectedRepairJob);
              }}
            >
              <PlusCircle size={16} />
              Add Status Update
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h5 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Timeline
              </h5>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => {
                  onHide();
                  handleStatusUpdate(selectedRepairJob);
                }}
              >
                <PlusCircle size={14} />
                Add Update
              </Button>
            </div>

            <div className="relative pl-6 space-y-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-muted">
              {statusEntries.map((entry, index) => (
                <div key={index} className="relative">
                  <div className="absolute -left-[21px] top-1.5 h-3 w-3 rounded-full border-2 border-background bg-primary shadow-sm" />
                  <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Badge variant="secondary" className="font-mono">
                        #{statusEntries.length - index}
                      </Badge>
                      <time className="text-xs text-muted-foreground">
                        {entry.created_at
                          ? new Date(entry.created_at).toLocaleString()
                          : "N/A"}
                      </time>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                          Status
                        </p>
                        <Badge
                          variant="outline"
                          className="bg-primary/5 text-primary border-primary/20 capitalize"
                        >
                          {(entry.status || "N/A").replace("_", " ")}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                          Updated By
                        </p>
                        <p className="text-sm font-semibold">
                          {entry.changedBy?.full_name ||
                            entry.changedBy?.name ||
                            entry.changed_by?.full_name ||
                            entry.changed_by?.name ||
                            "System"}
                        </p>
                      </div>
                    </div>

                    {entry.notes && (
                      <div className="pt-2 border-t border-muted">
                        <p className="text-[10px] uppercase font-bold text-muted-foreground mb-1">
                          Notes
                        </p>
                        <p className="text-sm text-foreground italic bg-background/50 p-2 rounded border border-dashed">
                          &ldquo;{entry.notes}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

StatusHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedRepairJob: PropTypes.shape({
    item_description: PropTypes.string,
  }),
  statusEntries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      status: PropTypes.string,
      created_at: PropTypes.string,
      notes: PropTypes.string,
      changedBy: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  handleStatusUpdate: PropTypes.func.isRequired,
};

export default StatusHistoryModal;
